/** Jaro-Winkler string similarity in [0,1]. */
export function jaroWinkler(s1: string, s2: string): number {
  if (s1 === s2) return 1;
  if (s1.length === 0 || s2.length === 0) return 0;
  const matchDist = Math.max(0, Math.floor(Math.max(s1.length, s2.length) / 2) - 1);
  const s1m = new Array(s1.length).fill(false);
  const s2m = new Array(s2.length).fill(false);
  let matches = 0;
  for (let i = 0; i < s1.length; i++) {
    const start = Math.max(0, i - matchDist);
    const end = Math.min(i + matchDist + 1, s2.length);
    for (let j = start; j < end; j++) {
      if (s2m[j]) continue;
      if (s1[i] !== s2[j]) continue;
      s1m[i] = true;
      s2m[j] = true;
      matches++;
      break;
    }
  }
  if (matches === 0) return 0;
  let k = 0;
  let transpositions = 0;
  for (let i = 0; i < s1.length; i++) {
    if (!s1m[i]) continue;
    while (!s2m[k]) k++;
    if (s1[i] !== s2[k]) transpositions++;
    k++;
  }
  const jaro =
    (matches / s1.length + matches / s2.length + (matches - transpositions / 2) / matches) / 3;
  let prefix = 0;
  for (let i = 0; i < Math.min(4, s1.length, s2.length); i++) {
    if (s1[i] === s2[i]) prefix++;
    else break;
  }
  return jaro + prefix * 0.1 * (1 - jaro);
}

export interface RenameCandidate {
  readonly kind: string;
  readonly name: string;
  readonly fieldKeys: readonly string[];
}

/**
 * Composite score: name similarity weighted high, kind must match
 * (mismatch capped), field-set similarity adds confidence.
 */
export function scoreRename(b: RenameCandidate, a: RenameCandidate): number {
  const name = jaroWinkler(b.name, a.name);
  const typeOk = b.kind === a.kind;
  const bf = new Set(b.fieldKeys);
  const af = new Set(a.fieldKeys);
  const inter = [...bf].filter((k) => af.has(k)).length;
  const union = new Set([...bf, ...af]).size;
  const shape = union === 0 ? 1 : inter / union;
  const raw = 0.6 * name + 0.4 * shape;
  return typeOk ? raw : Math.min(raw, 0.5);
}

export const RENAME_THRESHOLD = 0.7;
